import React, { useState } from 'react'
import { Modal } from '@mui/material'
import { Formik, Form } from 'formik'
import tw from 'twin.macro'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { Button } from 'src/components/shared/Button'
import { FormikTextArea } from 'src/components/shared/form/formik/FormikTextArea'
import { CreateContractorNote, UpdateContractorNote } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { handleApiError } from 'src/utils/errorHandlers'
import { useDispatch } from 'react-redux'
import { ContractorNoteSchema } from './ContractorNoteForm.validator'

interface Props {
  isContractorNoteModalOpen: boolean
  closeContractorNoteModal(): void
  refreshContractorNotes(): void | Promise<void>
  contractorId: string
  initialNote?: string
  noteId?: string
}

interface FormValues {
  note: string
}

export const ContractorNoteModal = ({
  isContractorNoteModalOpen,
  closeContractorNoteModal,
  contractorId,
  refreshContractorNotes,
  initialNote = '',
  noteId,
}: Props) => {
  const dispatch = useDispatch()

  const [isLoading, setIsLoading] = useState(false)

  const isEditMode = Boolean(noteId)

  const initialValues: FormValues = { note: initialNote }

  const handleSubmit = async (values: FormValues) => {
    try {
      setIsLoading(true)

      if (isEditMode) {
        await createAsyncAction(
          dispatch,
          UpdateContractorNote.request({
            contractorId,
            noteId,
            note: values.note,
          }),
        )

        showToast('Note has been updated!')
      } else {
        await createAsyncAction(
          dispatch,
          CreateContractorNote.request({
            contractorId,
            note: values.note,
          }),
        )

        showToast('Note has been added!')
      }

      closeContractorNoteModal()
      await refreshContractorNotes()
    } catch (err) {
      handleApiError(err)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Modal
      open={isContractorNoteModalOpen}
      onClose={closeContractorNoteModal}
      aria-labelledby="note-modal-title"
      aria-describedby="note-modal-description"
      css={tw`fixed inset-0 flex items-center justify-center`}
    >
      <div css={tw`w-[336px] bg-white flex flex-col rounded-lg shadow-lg`}>
        <ModalHeader
          className={tw`h-11`}
          title={isEditMode ? 'Edit note' : 'New note'}
          onClose={closeContractorNoteModal}
        />
        <Formik
          initialValues={initialValues}
          validationSchema={ContractorNoteSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {() => (
            <Form css={tw`flex flex-col flex-1`}>
              <div css={tw`p-4 flex-1 overflow-y-auto`}>
                <FormikTextArea name="note" minRows={9} fullWidth placeholder="Enter your note" />
              </div>
              <div
                css={tw`px-4 py-3 flex justify-between border-0 border-t border-solid border-[#EDEDED]`}
              >
                <Button
                  variant="contained"
                  color="grey"
                  size="small"
                  onClick={closeContractorNoteModal}
                  loading={isLoading}
                  className={tw`w-[100px]`}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  type="submit"
                  loading={isLoading}
                  className={tw`w-[120px]`}
                >
                  {isEditMode ? 'Save' : 'Add note'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}
